/**
 * Personalized invite landing page (route: /invite/:code).
 *
 * Flow:
 *   • Patron shares /invite/<their-referral-code>.
 *   • Visitor lands here — backend's GET /api/referral/preview/:code returns
 *     the inviter's first name + current waitlist mode + a `copy` object
 *     holding every admin-editable string (CMS-driven).
 *   • We render the celebratory hero using `copy.invite_headline` (with the
 *     inviter's name already interpolated server-side).
 *   • CTA destination depends on waitlist mode:
 *       waitlist OPEN  → `/?ref=CODE` (homepage CTA prefilled)
 *       waitlist CLOSED → `/register?ref=CODE` (direct OTP signup)
 *   • Invalid code → fall through to homepage so we never 404 a share URL.
 *
 * The ref code is persisted to localStorage as `oodel_ref_code` so the
 * downstream form auto-populates even on slow networks.
 */
import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Logo from "../components/Logo";

const FALLBACK_COPY = {
  invite_overline: "✦ PERSONAL INVITE",
  invite_headline: "A friend invited you to oodel.",
  invite_subline_waitlist_open: "You'll claim a spot on the founding patron waitlist. When the first surveys go live, you both earn rewards.",
  invite_subline_waitlist_closed: "Create your account, complete your profile, and take your first survey — you'll both earn rewards.",
  invite_cta_waitlist_open: "Join the waitlist →",
  invite_cta_waitlist_closed: "Create your account →",
  invite_pillar_1_title: "Earn points",
  invite_pillar_1_body: "Every survey you complete pays. Cash out for Foodpanda, Daraz, EasyPaisa.",
  invite_pillar_2_title: "Stay anonymous",
  invite_pillar_2_body: "Your answers are aggregated. Your identity stays hidden.",
  invite_pillar_3_title: "Shape the data",
  invite_pillar_3_body: "Help build the most representative panel for Pakistan.",
  invite_already_member_text: "Already a member?",
  invite_signin_link_text: "Sign in instead",
  invite_code_label: "Invite code:",
  invite_invalid_title: "Hmm — that invite link's expired",
  invite_invalid_body: "No problem. You can still join the panel directly below.",
  invite_invalid_cta: "Visit oodel. →",
};

export default function InviteLanding() {
  const { code } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, preview: null });

  useEffect(() => {
    if (!code) { nav("/", { replace: true }); return; }
    // Persist immediately so signup forms read it even before the API call returns.
    try { localStorage.setItem("oodel_ref_code", code.toUpperCase()); } catch { /* no-op */ }
    api.get(`/referral/preview/${code}`)
      .then((r) => setState({ loading: false, preview: r.data }))
      .catch(() => setState({ loading: false, preview: { code_valid: false } }));
  }, [code, nav]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-neutral-400">Loading your invite…</div>
      </div>
    );
  }

  const p = state.preview || {};
  const copy = { ...FALLBACK_COPY, ...(p.copy || {}) };
  // Admin can disable the referral program entirely. Silently redirect to the
  // homepage so the feature feels like it doesn't exist — no "paused" message,
  // no broken invite. The shared link just becomes a homepage link.
  if (p.program_enabled === false) {
    return <Navigate to="/" replace />;
  }
  if (!p.code_valid) {
    // Soft-fail: send them to the homepage. Ref code already in localStorage.
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-6 text-center">
        <div className="max-w-md">
          <Logo size="md" />
          <h1 className="text-2xl font-black tracking-tight mt-6" data-testid="invite-invalid-title">{copy.invite_invalid_title}</h1>
          <p className="text-neutral-600 mt-3 text-sm" data-testid="invite-invalid-body">{copy.invite_invalid_body}</p>
          <Link to="/" className="inline-block mt-6 bg-mango text-white border-2 border-black px-6 py-3 font-bold hard-shadow hover:bg-black transition-colors" data-testid="invite-invalid-cta">
            {copy.invite_invalid_cta}
          </Link>
        </div>
      </div>
    );
  }

  const ctaLabel = p.waitlist_open ? copy.invite_cta_waitlist_open : copy.invite_cta_waitlist_closed;
  const ctaTo = p.waitlist_open ? `/?ref=${p.code}` : `/register?ref=${p.code}`;
  const subline = p.waitlist_open ? copy.invite_subline_waitlist_open : copy.invite_subline_waitlist_closed;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="invite-landing">
      {/* Background grain + glow */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
      }} />
      <div className="absolute -top-32 -left-32 w-96 h-96 bg-mango/20 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-mango/10 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-6 py-16 md:py-24">
        <Logo size="md" inverted />

        <div className="mt-16 md:mt-24">
          <div className="overline text-mango mb-6" data-testid="invite-overline">{copy.invite_overline}</div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6" data-testid="invite-headline">
            {/* Render headline with the inviter's first name highlighted in mango.
                If the template doesn't contain the name (e.g. admin removed
                {name}), fall back to plain rendering. */}
            {p.inviter_first_name && copy.invite_headline.includes(p.inviter_first_name) ? (
              renderHighlighted(copy.invite_headline, p.inviter_first_name)
            ) : (
              copy.invite_headline
            )}
          </h1>
          <p className="text-lg md:text-xl text-neutral-300 max-w-xl leading-relaxed mb-12" data-testid="invite-subline">
            {subline}
          </p>

          <Link
            to={ctaTo}
            data-testid="invite-cta"
            className="inline-flex items-center gap-2 bg-mango text-black border-2 border-mango px-8 py-5 text-lg font-bold hard-shadow hover:bg-white hover:border-white transition-colors"
          >
            {ctaLabel}
          </Link>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <Pillar title={copy.invite_pillar_1_title} body={copy.invite_pillar_1_body} />
            <Pillar title={copy.invite_pillar_2_title} body={copy.invite_pillar_2_body} />
            <Pillar title={copy.invite_pillar_3_title} body={copy.invite_pillar_3_body} />
          </div>

          <p className="mt-12 text-xs text-neutral-500 max-w-md">
            {copy.invite_already_member_text}{" "}
            <Link to="/login" className="text-mango hover:text-white underline">{copy.invite_signin_link_text}</Link>.
            {" "}{copy.invite_code_label} <span className="font-mono text-neutral-400">{p.code}</span>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Render a string with the first occurrence of `highlight` wrapped in a mango span. */
function renderHighlighted(text, highlight) {
  const idx = text.indexOf(highlight);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-mango">{highlight}</span>
      {text.slice(idx + highlight.length)}
    </>
  );
}

function Pillar({ title, body }) {
  return (
    <div className="border-l-2 border-mango pl-4">
      <div className="overline text-mango mb-2">{title}</div>
      <p className="text-sm text-neutral-300 leading-relaxed">{body}</p>
    </div>
  );
}
